import {BlockerContext, Separator, showNotification} from 'platform/components';
import {z} from 'zod';

import {useCallback} from 'react';
import {useSelector} from 'react-redux';

import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {handleApiError} from '@dms/shared';
import {
  selectVehicleSeriesDefinitionDetail,
  updateVehicleSeriesDefinition,
  updateVehicleSeriesSystemDefinition,
  useApiDispatch,
  VehicleSeriesDefinitionRequestBody,
} from '@dms/teas';

import {useNavigate} from 'shared/web';

import {VehicleSeriesDefinitionFormType} from '../types';
import {VehicleSeriesDefinitionFormValidations} from './validations';
import {VehicleSeriesDefinitionFormButtons} from './VehicleSeriesDefinitionFormButtons';
import {VehicleSeriesDefinitionFormDefaultLayout} from './VehicleSeriesDefinitionFormDefaultLayout';
import {VehicleSeriesDefinitionFormWithLists} from './VehicleSeriesDefinitionFormWithLists';

export const VehicleSeriesDefinitionEdit = () => {
  const apiDispatch = useApiDispatch();
  const navigate = useNavigate();
  const definition = useSelector(selectVehicleSeriesDefinitionDetail);

  const handleSubmit = useCallback(
    async (
      values: VehicleSeriesDefinitionFormType,
      _setErrors: any, // eslint-disable-line typescript/no-explicit-any -- Pre-existing any
      blockerContext?: BlockerContext
    ) => {
      if (!definition) {
        return;
      }

      const updatePromise = definition.isSystem
        ? apiDispatch(updateVehicleSeriesSystemDefinition.action, {
            id: definition.id,
            requestBody: {name: values.name},
          })
        : (() => {
            const fullValues = values as z.infer<typeof VehicleSeriesDefinitionFormValidations>;
            return apiDispatch(updateVehicleSeriesDefinition.action, {
              id: definition.id,
              requestBody: {
                ...fullValues,
                vehicleType:
                  fullValues.vehicleType as VehicleSeriesDefinitionRequestBody['vehicleType'],
              },
            });
          })();

      await updatePromise
        .then(() => {
          showNotification.success(
            i18n.t('page.vehicleSeriesDefinition.notifications.definitionUpdated')
          );
          if (blockerContext?.wasSubmittedFromBlocker) {
            return;
          }
          navigate(settingsRoutes.vehicleSeriesDefinition);
        })
        .catch((error: {response: Error}) => {
          handleApiError(error?.response, {signalApiError: blockerContext?.reportApiFailure});
        });
    },
    [navigate, apiDispatch, definition]
  );

  return (
    <VehicleSeriesDefinitionFormDefaultLayout>
      <VehicleSeriesDefinitionFormWithLists
        cancelRoute={settingsRoutes.vehicleSeriesDefinition}
        onSubmit={handleSubmit}
        initialValues={definition}
        isSystem={definition?.isSystem}
        renderFooter={({navigateWithoutBlocker, cancelRoute}) => (
          <>
            <Separator />
            <VehicleSeriesDefinitionFormButtons
              onCancel={cancelRoute ? () => navigateWithoutBlocker(cancelRoute) : undefined}
              submitLabel={i18n.t('general.actions.update')}
              cancelTestId={testIds.settings.vehicleEditSeriesDefinition('cancel')}
              submitTestId={testIds.settings.vehicleEditSeriesDefinition('update')}
            />
          </>
        )}
      />
    </VehicleSeriesDefinitionFormDefaultLayout>
  );
};
